import { Alert, Box, Button, IconButton, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import React from 'react';
import UserFetch, { UserPost } from '../../userFetch';
import CreateOwnerDialog, { OwnerFormData } from './CreateOwnerDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import { invalidateOwnerNamesCache } from '../../hooks/useOwnerNames';

interface WifiNetwork {
  ssid: string;
  password: string;
}

interface Owner {
  id: number;
  name: string;
  default_config: string | null;
  wifi_networks: WifiNetwork[];
  notes: string | null;
  api_token: string | null;
  server_host: string | null;
  country_code: string | null;
  created: string;
}

function OwnersPage() {
  const [owners, setOwners] = React.useState<Owner[]>([]);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editData, setEditData] = React.useState<OwnerFormData | null>(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState<Owner | null>(null);
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchOwners = () => {
    UserFetch('/api/owners')
      .then((response) => response.json())
      .then((data: Owner[]) => setOwners(data));
  };

  React.useEffect(() => {
    fetchOwners();
  }, []);

  const handleSave = async (data: OwnerFormData) => {
    const isEdit = editData?.id !== undefined;
    const response = isEdit
      ? await UserFetch(`/api/owners/${editData!.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        })
      : await UserPost('/api/owners', data);

    if (response.ok) {
      setDialogOpen(false);
      setEditData(null);
      invalidateOwnerNamesCache();
      fetchOwners();
    } else {
      const message = await response.text().catch(() => 'Save failed');
      setSnackbar({ message: message || `Save failed (${response.status})`, severity: 'error' });
    }
  };

  const handleDelete = (owner: Owner) => {
    UserFetch(`/api/owners/${encodeURIComponent(owner.name)}`, { method: 'DELETE' })
      .then(async (response) => {
        if (response.ok) {
          setDeleteConfirm(null);
          invalidateOwnerNamesCache();
          fetchOwners();
        } else if (response.status === 409) {
          const message = await response.text();
          setDeleteConfirm(null);
          setSnackbar({ message, severity: 'error' });
        }
      });
  };

  const handleEdit = (owner: Owner) => {
    setEditData({
      id: owner.id,
      name: owner.name,
      default_config: owner.default_config,
      wifi_networks: owner.wifi_networks || [],
      notes: owner.notes,
      api_token: owner.api_token,
      server_host: owner.server_host,
      country_code: owner.country_code,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditData(null);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const truncateConfig = (config: string | null) => {
    if (!config) return '-';
    return config.length > 60 ? config.substring(0, 60) + '...' : config;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Owners</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          size="small"
        >
          Add Owner
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>WiFi</TableCell>
              <TableCell>Default Config</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {owners.map((owner) => (
              <TableRow key={owner.id}>
                <TableCell>{owner.name}</TableCell>
                <TableCell>
                  {(owner.wifi_networks?.length || 0) > 0
                    ? owner.wifi_networks.map(w => w.ssid).join(', ')
                    : '-'}
                </TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {truncateConfig(owner.default_config)}
                </TableCell>
                <TableCell>{formatDate(owner.created)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(owner)}
                    title="Edit owner"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteConfirm(owner)}
                    title="Delete owner"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateOwnerDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSave}
        editData={editData}
      />

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete Owner"
        message={`Are you sure you want to delete owner "${deleteConfirm?.name}"?`}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default OwnersPage;
